<template>
  <el-tabs
    class="campaign-editor"
    :value="activeTab"
    :before-leave="handleBeforeTabLeave"
    @tab-click="handleTabClick"
  >
    <el-tab-pane
      :name="PAGE_TAB.NAME"
      :label="$t('admin.crm.label.campaign.name')"
    >
      <Editor
        class="name-editor"
        ref="nameEditor"
        no-header
        :loading.sync="loading"
        :model="campaign"
        :formLines="formLines"
        @cancel="handleCancel"
        @save="handleSave"
      >
        <template #microSiteId="{ dataKey, placeholder }">
          <el-select v-model="campaign[dataKey]" :disabled="isEditMode" :placeholder="placeholder">
            <el-option
              v-for="(item, key) in micrositeList"
              :key="key"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </template>
      </Editor>
    </el-tab-pane>
    <el-tab-pane
      v-if="isEditMode"
      :name="PAGE_TAB.SHARE"
      :label="$t('admin.crm.label.campaign.shareConfigHeader')"
    >
      <div v-rect-loading="loading" class="share-config-container">
        <div class="preview-language">
          <div
            v-for="(item, index) in previewLocaleList"
            class="language-item"
            :key="index"
            :class="item.dataKey === shareLocale && 'is-active'"
            @click="handlePreviewLanguage(item)"
          >{{ item.label }}</div>
        </div>
        <div class="container-header">
          <div class="title">{{ $t('admin.crm.label.campaign.shareConfigHeader') }}</div>
          <el-switch v-model="weChatShareEnabled" class="config-status-switch" :width="34"></el-switch>
        </div>
        <div class="tip-message">
          {{ $t('admin.crm.message.microsite.shareTip') }}
        </div>
        <div class="share-config">
          <div class="share-image config-line">
            <span class="config-label">{{ $t('admin.crm.label.microsite.shareImage') }}</span>
            <div class="image-container" :class="!weChatShareConfig.imageUrl && 'no-image'">
              <img :src="weChatShareConfig.imageUrl"/>
              <div class="add-btn" @click="handleAddImageBtnClick">
                <span class="btn-plus">+</span>
                <span class="btn-label">{{ $t('admin.crm.btn.addImage') }}</span>
              </div>
              <input
                ref="shareImageFile"
                type="file"
                :accept="acceptFileType.join(',')"
                @change="handleImageFileChange(false)"
              />
            </div>
          </div>
          <div class="share-friends-title config-line">
            <span class="config-label">{{ $t('admin.crm.placeholder.microsite.shareFriendsTitle') }}</span>
            <el-input v-model="weChatShareConfig.friendsTitle"></el-input>
          </div>
          <div class="share-description config-line">
            <span class="config-label">{{ $t('admin.crm.placeholder.microsite.shareDesc') }}</span>
            <el-input
              v-model="weChatShareConfig.description"
              type="textarea"
              :autosize="{ minRows: 3 }"
            ></el-input>
          </div>
          <div class="share-moments-title config-line">
            <span class="config-label">{{ $t('admin.crm.placeholder.microsite.shareMomentsTitle') }}</span>
            <el-input v-model="weChatShareConfig.momentsTitle"></el-input>
          </div>
        </div>
        <div class="bottom-btn-group">
          <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
          <el-button class="save-btn" @click="handleWeChatShareSave">{{ $t('common.btn.save') }}</el-button>
        </div>
      </div>
    </el-tab-pane>
    <el-tab-pane
      v-if="isEditMode"
      :name="PAGE_TAB.SEND"
      :label="$t('admin.crm.label.campaign.notificationHeader')"
    >
      <List
        title=""
        createBtnLabelI18nKey="admin.crm.btn.campaign.send"
        no-pagination
        no-action
        :loading="loading"
        :listData="notificationList"
        :tableColumns="tableColumns"
        @create="handleSendCampaign"
      ></List>
    </el-tab-pane>
  </el-tabs>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapGetters, mapActions } from 'vuex'
import { isEn } from '@/i18n'
import { ACCEPT_FILE_TYPE, ACCEPT_FILE_SIZE } from '@/constant/crm'
import { uploadFile, generateCrmCampaignFileKey } from '@/utils/qiniu'
import { validateEmail } from '@/utils/validator'
import Editor from '../editor.vue'
import List from '@/components/tableList/allowDragSortTable.vue'

const COMPANY_RW_ROOT_ID = 8223
const PAGE_TAB = {
  NAME: 'name',
  SHARE: 'share',
  SEND: 'send'
}

export default {
  components: {
    Editor,
    List
  },
  data () {
    const vm = this
    return {
      PAGE_TAB,
      loadingCount: 0,
      account: {},
      campaign: {},
      micrositeList: [],
      notificationList: [],
      previewLocaleList: [
        {
          dataKey: 'cn',
          label: '简体中文'
        },
        {
          dataKey: 'en',
          label: 'English'
        }
      ],
      shareLocale: '',
      acceptFileType: ACCEPT_FILE_TYPE,
      imageUploader: {

      },
      weChatShareEnabled: false,
      weChatShareConfig: {
        get imageUrl () {
          const locale = vm.shareLocale
          if (locale) {
            const path = this[locale].imageUrl || null
            if (!path || path.includes('http')) {
              return path
            } else {
              return `${process.env.VUE_APP_QINIU_DOMAIN}${path}`
            }
          }
          return null
        },
        get friendsTitle () {
          const locale = vm.shareLocale
          if (locale) {
            return this[locale].friendsTitle
          }
          return null
        },
        get description () {
          const locale = vm.shareLocale
          if (locale) {
            return this[locale].description
          }
          return null
        },
        get momentsTitle () {
          const locale = vm.shareLocale
          if (locale) {
            return this[locale].momentsTitle
          }
          return null
        },
        set imageUrl (value) {
          const locale = vm.shareLocale
          if (locale) {
            this[locale].imageUrl = value
          }
        },
        set friendsTitle (value) {
          const locale = vm.shareLocale
          if (locale) {
            this[locale].friendsTitle = value
          }
        },
        set description (value) {
          const locale = vm.shareLocale
          if (locale) {
            this[locale].description = value
          }
        },
        set momentsTitle (value) {
          const locale = vm.shareLocale
          if (locale) {
            this[locale].momentsTitle = value
          }
        },
        en: {
          imageUrl: '',
          friendsTitle: '',
          description: '',
          momentsTitle: ''
        },
        cn: {
          imageUrl: '',
          friendsTitle: '',
          description: '',
          momentsTitle: ''
        }
      },
      qrCodeDialog: {
        show: false,
        headerLabel: '',
        qrImageName: '',
        qrLogo: '',
        qrData: '',
        qrSize: 250
      },
      exportDialog: {
        show: false,
        siteId: '',
        campaignName: '',
        micrositeName: ''
      }
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    activeTab () {
      const tab = this.$route.query.tab
      if (!Object.values(PAGE_TAB).includes(tab)) {
        return PAGE_TAB.NAME
      }
      return tab
    },
    loading: {
      get () {
        return this.loadingCount > 0
      },
      set (val) {
        this.loadingCount += val ? 1 : -1
      }
    },
    isRWCompany () {
      let result = false
      if (this.campaign?.company?.id === COMPANY_RW_ROOT_ID || this.campaign?.company?.root_id === COMPANY_RW_ROOT_ID) {
        result = true
      }
      if (this.account && (this.account.company_id === COMPANY_RW_ROOT_ID || this.account.root_company_id === COMPANY_RW_ROOT_ID)) {
        result = true
      }
      return result
    },
    hasFormMicroSites () {
      return _.filter(this.micrositeList, { has_form: true })
    },
    hasReceiptEmail () {
      return this.isRWCompany && _.find(this.hasFormMicroSites, { id: this.campaign.microsite_id })
    },
    formLines () {
      const lines = {
        name: {
          labelI18nKey: 'admin.crm.label.campaign.name',
          placeholderI18nKey: 'admin.crm.placeholder.campaign.name',
          rules: [{ required: true, message: this.$t('admin.crm.message.campaign.nameRequired') }]
        },
        microSiteId: {
          dataKey: 'microsite_id',
          labelI18nKey: 'admin.crm.label.campaign.microsite',
          placeholderI18nKey: 'admin.crm.placeholder.campaign.microsite',
          rules: [{ required: true, message: this.$t('admin.crm.message.campaign.micrositeRequired') }]
        },
        receiptEmail: {
          hidden: !this.hasReceiptEmail,
          dataKey: 'receipt_email',
          labelI18nKey: 'admin.crm.label.campaign.receiptEmail',
          placeholderI18nKey: 'admin.crm.placeholder.campaign.receiptEmail',
          rules: [{ validator: this.checkReceiptEmail, trigger: 'blur' }]
        }
      }
      return lines
    },
    tableColumns () {
      const columns = [
        {
          labelI18nKey: 'admin.crm.label.campaign.name',
          dataKey: 'campaign.name'
        },
        {
          labelI18nKey: 'admin.crm.label.microsite.micrositeName',
          dataKey: 'campaign.microsite.name'
        },
        {
          hidden: this.alumniTemplateVisibility,
          labelI18nKey: 'admin.crm.label.campaignNotification.talentGroup',
          dataKey: 'talent_group.name'
        },
        {
          hidden: !this.alumniTemplateVisibility,
          labelI18nKey: 'admin.crm.label.campaignNotification.alumniSearchType',
          dataKey: 'send_alumni_type',
          formatter: (value) => {
            let i18nKey = 'admin.crm.option.alumniSearchTypeOption.'
            if (value === 'IN_TALENT_POOL') {
              i18nKey += 'InTalentPool'
            } else if (value === 'NOT_IN_TALENT_POOL') {
              i18nKey += 'notInTalentPool'
            } else if (value === 'ALL_ALUMNI') {
              i18nKey += 'allInTalentPool'
            } else {
              return ''
            }
            return this.$t(i18nKey)
          }
        },
        {
          labelI18nKey: 'admin.crm.label.campaign.date',
          dataKey: 'created_time',
          formatter: (value) => moment(value).format('YYYY-MM-DD')
        }
      ]
      return _.filter(columns, (col) => !col.hidden)
    },
    alumniTemplateVisibility () {
      return this.campaign?.microsite?.visibility === 'A'
    }
  },
  beforeCreate () {
    const tab = this.$route.query.tab
    if (!Object.values(PAGE_TAB).includes(tab)) {
      this.$router.replace({ query: { ...this.$route.query, tab: PAGE_TAB.NAME } })
    }
  },
  created () {
    this.account = this.getCurrentAccount()
    this.refreshPage()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('crm', ['getCampaignById', 'createCampaign', 'updateCampaign', 'searchMicrosite', 'searchCampaignNotification']),
    handleTabClick (pane) {
      const tabName = pane.paneName
      if (tabName === this.activeTab) {
        return
      }
      if (this.handleBeforeTabLeave()) {
        this.$router.push({ query: { ...this.$route.query, tab: tabName } })
      }
    },
    handleBeforeTabLeave (newTabName) {
      if (this.activeTab !== PAGE_TAB.NAME) {
        return true
      }
      let result = true
      this.$refs.nameEditor.validate((isSuccess) => {
        result = isSuccess
      })
      return result
    },
    refreshPage () {
      this.loading = true
      this.shareLocale = isEn() ? 'en' : 'cn'
      const promiseList = []
      if (this.isEditMode) {
        promiseList.push(this.initCampaign(), this.initNotificationData())
      } else {
        promiseList.push(this.initMicrositeData())
      }
      Promise.all(promiseList).finally(() => {
        this.loading = false
      })
    },
    async initNotificationData () {
      const params = { }
      const resp = await this.searchCampaignNotification({ campaignId: this.$route.params.campaignId, params })
      if (resp) {
        this.notificationList = resp
      }
    },
    async initMicrositeData () {
      const resp = await this.searchMicrosite({ is_published: true })
      if (resp) {
        this.micrositeList = resp
      }
    },
    async initCampaign () {
      const campaignId = this.$route.params.campaignId
      const resp = await this.getCampaignById({ campaignId, params: { need_company: true } })
      if (resp) {
        const campaign = resp
        this.weChatShareEnabled = resp.wechat_share_enabled
        const weChatShareConfig = JSON.parse(resp.wechat_share_config)
        if (weChatShareConfig) {
          this.weChatShareConfig.en = weChatShareConfig.en
          this.weChatShareConfig.cn = weChatShareConfig.cn
        }
        campaign.microsite_id = resp.microsite.id
        this.campaign = campaign
        this.micrositeList = [campaign.microsite]
      }
    },
    handleCancel () {
      this.$router.push({ name: 'crmCampaignList' })
    },
    async handleSave (closeLoading) {
      try {
        const data = {
          name: this.campaign.name
        }
        if (this.isRWCompany) {
          data.receipt_email = this.campaign.receipt_email
        }
        let saveFunction = null
        if (this.isEditMode) {
          data.id = this.campaign.id
          saveFunction = this.updateCampaign
        } else {
          data.microsite_id = this.campaign.microsite_id
          saveFunction = this.createCampaign
        }
        const resp = await saveFunction(data)
        if (resp) {
          this.$message.success(this.$t('common.message.saveSuccess'))
          if (!this.isEditMode) {
            this.campaign.id = resp.id
            this.$router.push({ name: 'editCrmCampaign', params: { campaignId: resp.id }, query: { ...this.$route.query, tab: PAGE_TAB.SHARE } })
          }
        } else {
          this.$message({ message: this.$t('admin.crm.message.microsite.requestError'), type: 'error' })
        }
      } finally {
        closeLoading && closeLoading()
      }
    },
    handlePreviewLanguage (item) {
      this.shareLocale = item.dataKey
    },
    handleAddImageBtnClick () {
      this.$refs.shareImageFile.click()
    },
    handleImageFileChange () {
      const fileInput = this.$refs.shareImageFile
      const file = fileInput.files[0]
      if (!file) {
        return
      }
      if (!ACCEPT_FILE_TYPE.includes(file.type)) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t('admin.crm.message.microsite.wrongImageType')
        })
        fileInput.value = null
        return
      }
      if (file.size > ACCEPT_FILE_SIZE) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t('admin.crm.message.microsite.wrongImageSize', { size: 2 })
        })
        fileInput.value = null
        return
      }
      this.imageUploader[this.shareLocale] = file
      this.weChatShareConfig.imageUrl = window.URL.createObjectURL(file)
    },
    handleSendCampaign () {
      this.$router.push({ path: `/v-admin/crm-management/campaign-management/send/${this.campaign.id}`, query: { back: this.$route.fullPath } })
    },
    async handleWeChatShareSave () {
      this.loading = true
      try {
        const weChatShareConfig = {
          en: this.weChatShareConfig.en,
          cn: this.weChatShareConfig.cn
        }
        const enImage = this.imageUploader.en
        const cnImage = this.imageUploader.cn
        if (enImage) {
          const fileKey = generateCrmCampaignFileKey(this.campaign.id)
          await uploadFile(enImage, fileKey)
          weChatShareConfig.en.imageUrl = fileKey
        }
        if (cnImage) {
          const fileKey = generateCrmCampaignFileKey(this.campaign.id)
          await uploadFile(cnImage, fileKey)
          weChatShareConfig.cn.imageUrl = fileKey
        }

        const data = {
          id: this.campaign.id,
          wechat_share_enabled: this.weChatShareEnabled,
          wechat_share_config: JSON.stringify(weChatShareConfig)
        }
        const resp = await this.updateCampaign(data)
        if (resp) {
          this.$message.success(this.$t('common.message.saveSuccess'))
        } else {
          this.$message.error(this.$t('admin.crm.message.microsite.requestError'))
        }
      } finally {
        this.loading = false
      }
    },
    checkReceiptEmail (rule, value, callback) {
      let errMsg
      try {
        if (value) {
          errMsg = validateEmail(rule, value)
        }
      } finally {
        if (errMsg) {
          callback(new Error(errMsg))
        } else {
          callback()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.campaign-editor {
  position: relative;

  ::v-deep {
    .el-tabs__active-bar {
      transition: transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1),
                  width 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

    }

    .el-tabs__item {
      height: 30px;
      line-height: 21px;
      padding-bottom: 8px;
      font-size: 15px;
      font-weight: 400;

      &:not(.is-active) {
        color: #909399;
      }
    }
  }

  .name-editor {
    ::v-deep .form-container {
      margin-top: 15px;
    }
  }

  .share-config-container {
    width: 600px;
    margin: 15px auto 0;

    .preview-language {
      text-align: right;

      .language-item {
        display: inline-block;
        color: #0096cc;
        vertical-align: middle;
        line-height: 1;
        cursor: pointer;

        &.is-active {
          color: #303133;
          cursor: default;
        }

        &:last-child {
          margin-left: 12px;
          padding-left: 12px;
          border-left: 1px solid #ddd;
        }
      }
    }

    .container-header {
      margin-top: 20px;
      position: relative;

      .title {
        line-height: 22px;
        color: #303133;
        font-size: 16px;
        font-weight: 500;
      }

      .config-status-switch {
        display: flex;
        position: absolute;
        right: 0;
        top: 1px;

        ::v-deep {
          &.is-checked  {
            .el-switch__core {
              &::after {
                margin-left: -15px;
              }
            }
          }
          .el-switch__core {
            height: 16px;

            &::after {
              width: 14px;
              height: 14px;
              top: 0;
            }
          }
        }
      }
    }

    .tip-message {
      margin-top: 16px;
    }

    .share-config {
      margin-top: 20px;

      .config-line {
        display: flex;

        .config-label {
          display: inline-block;
          width: 114px;
          margin-right: 12px;
          vertical-align: middle;
          line-height: 32px;
          flex-grow: 0;
          flex-shrink: 0;
          text-align: right;
        }
      }

      .share-image {
        .config-label {
          line-height: 100px;
        }

        .image-container {
          width: 100px;
          height: 100px;
          border-radius: 6px;
          overflow: hidden;
          vertical-align: middle;
          position: relative;

          &:not(.no-image):hover {
            .add-btn {
              display: block;
              background: rgba($color: #ffffff, $alpha: 0.75);
            }
          }

          &.no-image {
            .add-btn {
              display: block;
            }

            img {
              visibility: hidden;
            }
          }

          .add-btn {
            display: none;
            width: 100%;
            height: 100%;
            border: 1px dashed #0096cc;
            border-radius: 6px;
            text-align: center;
            color: #606266;
            background: #fafafa;
            cursor: pointer;
            position: absolute;
            top: 0;
            left: 0;

            .btn-plus {
              display: block;
              margin: 20px 0 14px;
              font-size: 22px;
            }

            .btn-label {
              font-size: 12px;
              line-height: 17px;
            }
          }

          img {
            display: block;
            width: 100%;
            height: 100%;
          }

          input {
            display: none;
            visibility: hidden;
          }
        }

      }

      ::v-deep {
        .el-input__inner {
          height: 32px;
          line-height: 32px;
          border-radius: 2px;
        }

        .el-textarea__inner {
          padding: 5px 15px;
          border-radius: 2px;
        }
      }
      .share-friends-title {
        margin-top: 14px;
      }

      .share-description {
        margin-top: 20px;
      }

      .share-moments-title {
        margin-top: 20px;
      }
    }
    .config-body {
      .title-container {
        display: none;
      }

    }
  }

  .bottom-btn-group {
    display: flex;
    margin-top: 40px;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }

  .notication-container {
    .container-header {
      margin-top: 35px;
      font-weight: bold;
      font-size: 16px;
      color: #333;
    }

    .header-divider {
      margin-top: 8px;
    }
  }
}
.page-en {
  .campaign-editor {
    .share-config-container {
      .share-config {
        .config-line {
          .config-label {
            width: 164px;
          }
        }
      }
    }
  }
}
</style>
